import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import AddIcon from "@mui/icons-material/Add"
import { useNavigate } from "react-router-dom"
import { addDoc, collection, deleteDoc, doc, getDoc, onSnapshot, orderBy, query } from "firebase/firestore"

import { db } from "../../firebase"
import { LoadingService } from "react-ui-components/components/LoadingService"
import { PopupService } from "react-ui-components/components/PopupService"

import { DEFAULT_CONFIG, WheelLocationFormData } from "./wheelPrizeTypes"

const WHEEL_CONFIGS_COLLECTION = "wheelConfigs"
const LEGACY_CONFIG_COLLECTION = "app-configurations"
const LEGACY_CONFIG_DOC_ID = "wheelConfig"

type WheelListRow = {
  id: string
  name: string
  location: WheelLocationFormData
  language: string
  isEnabled: boolean
  isDefault: boolean
}

const formatLocation = (location: WheelLocationFormData | undefined): string => {
  const parts = [location?.city, location?.state, location?.country].filter(Boolean)
  return parts.length > 0 ? parts.join(", ") : "Any location"
}

const formatLanguage = (language: string | undefined): string => (language ? language.toUpperCase() : "Any language")

/**
 * Lists the `wheelConfigs/{id}` docs an admin has created — each scoped to a location/language
 * combination — mirroring how Featured Businesses and Raffles list their location-scoped
 * content. See WheelConfig for the per-wheel editor.
 */
export const WheelList = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState<WheelListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [legacyConfigExists, setLegacyConfigExists] = useState(false)

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, WHEEL_CONFIGS_COLLECTION), orderBy("name")),
      (snapshot) => {
        setRows(
          snapshot.docs.map((snap) => {
            const data = snap.data() as any
            return {
              id: snap.id,
              name: typeof data.name === "string" && data.name ? data.name : "Untitled wheel",
              location: data.location && typeof data.location === "object" ? data.location : {},
              language: typeof data.language === "string" ? data.language : "",
              isEnabled: data.isEnabled === true,
              isDefault: data.isDefault === true,
            }
          }),
        )
        setLoading(false)
      },
      (error) => {
        console.error("Error loading wheels:", error)
        PopupService.pushPopup(error.message)
        setLoading(false)
      },
    )

    return unsubscribe
  }, [])

  useEffect(() => {
    getDoc(doc(db, LEGACY_CONFIG_COLLECTION, LEGACY_CONFIG_DOC_ID))
      .then((snapshot) => setLegacyConfigExists(snapshot.exists()))
      .catch(() => setLegacyConfigExists(false))
  }, [])

  const hasDefault = useMemo(() => rows.some((row) => row.isDefault), [rows])

  const handleAdd = async () => {
    LoadingService.setLoading(true)
    try {
      const created = await addDoc(collection(db, WHEEL_CONFIGS_COLLECTION), {
        ...DEFAULT_CONFIG,
        name: "New wheel",
      })
      navigate(`/wheel-config/${created.id}`)
    } catch (error: any) {
      PopupService.pushPopup(error.message)
    } finally {
      LoadingService.setLoading(false)
    }
  }

  const handleImportLegacy = async () => {
    LoadingService.setLoading(true)
    try {
      const snapshot = await getDoc(doc(db, LEGACY_CONFIG_COLLECTION, LEGACY_CONFIG_DOC_ID))
      const legacyData = snapshot.exists() ? (snapshot.data() as any) : {}
      const created = await addDoc(collection(db, WHEEL_CONFIGS_COLLECTION), {
        ...DEFAULT_CONFIG,
        ...legacyData,
        name: "Default (migrated)",
        location: {},
        language: "",
        isDefault: true,
      })
      navigate(`/wheel-config/${created.id}`)
    } catch (error: any) {
      PopupService.pushPopup(error.message)
    } finally {
      LoadingService.setLoading(false)
    }
  }

  const handleDelete = (row: WheelListRow) => {
    PopupService.setConfirm(`Delete wheel "${row.name}"? This cannot be undone.`, (answer) => {
      if (!/ok/i.test(answer || "")) {
        return
      }
      ;(async () => {
        LoadingService.setLoading(true)
        try {
          await deleteDoc(doc(db, WHEEL_CONFIGS_COLLECTION, row.id))
        } catch (error: any) {
          PopupService.pushPopup(error.message)
        } finally {
          LoadingService.setLoading(false)
        }
      })()
    })
  }

  if (loading) {
    return null
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Box component="h1" sx={{ fontSize: 24, fontWeight: 600, m: 0 }}>
            Daily Spin Wheels
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Create one wheel per location/language combination. The wheel marked "default"
            applies whenever nothing more specific matches a user.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Add wheel
        </Button>
      </Box>

      {!hasDefault && rows.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No wheel is marked as default — users whose location/language don't match any wheel
          below won't see one. Open a wheel and enable "Default wheel" on the Setup tab.
        </Alert>
      )}

      {rows.length === 0 && legacyConfigExists && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleImportLegacy}>
              Import
            </Button>
          }
        >
          An existing single-wheel configuration was found. Import it as your default wheel to
          get started.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary">No wheels yet. Add one to get started.</Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/wheel-config/${row.id}`)}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{formatLocation(row.location)}</TableCell>
                <TableCell>{formatLanguage(row.language)}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Chip
                      size="small"
                      label={row.isEnabled ? "Enabled" : "Disabled"}
                      color={row.isEnabled ? "success" : "default"}
                    />
                    {row.isDefault && <Chip size="small" label="Default" color="primary" />}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation()
                        handleDelete(row)
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default WheelList
